import { ApolloCache, ApolloClient } from "@apollo/client";
import { GET_PROMO_DEDUCTION_MATCH_SUMMARY } from "@/core/graphql/promotion/getPromoDeductionMatchSummary";
import { PUT_PROMO_DEDUCTION_APPLIED } from "@/core/graphql/promotion/putPromoDeductionApplied";
import { GET_PROMOTIONS } from "@/core/graphql/promotion/getPromotions";
import {
  IGetPromoDeductionApplySummaryParams,
  IGetPromoDeductionMatchSummaryParams,
  IGetPromoProductsByPromoEventIdParams,
  IGetPromosParams,
  IPromoDeductionApplySummaryResponse,
  IPromoDeductionMatchSummary,
  IPromoMatchSummary,
  IPromoPaginatedResponse,
  IPromoProductsByPromoEventIdPaginatedResponse,
  IPromotionRepository,
  IPutPromoDeductionAppliedParams,
  IPutPromoDeductionAppliedResult,
} from "@/core/repositories/promotion";
import { ApplicationError } from "@/infrastructure/errors/ApplicationError";
import { GET_PROMO_PRODUCTS_BY_PROMO_EVENT_ID } from "@/core/graphql/promotion/getPromoProductsByPromoEventId";
import { GET_PROMO_DEDUCTION_APPLY_SUMMARY } from "@/core/graphql/promotion/getPromoDeductionApplySummary";
import { Deduction, DeductionProps } from "@/core/entities/deduction";
import { PromotionDeductionProps } from "@/core/entities/promotion/PromotionDeduction";

interface IPromoDeductionMatchSummaryQueryResponse {
  promoDeductionMatchSummary: IPromoDeductionMatchSummary[];
}

interface IPutPromoDeductionAppliedMutationResponse {
  putPromoDeductionApplied: {
    summaries: IPromoDeductionMatchSummary[];
    deduction: DeductionProps;
  };
}

interface IPromotionsQueryResponse {
  promotions: IPromoPaginatedResponse;
}

interface IPromoProductsByPromoEventIdQueryResponse {
  getPromoProductsByPromoEventId: IPromoProductsByPromoEventIdPaginatedResponse;
}

interface IGetPromoDeductionApplySummaryQueryResponse {
  getPromoDeductionApplySummary: {
    totalDeductionsClaimed: number;
    totalDeductionsAmount: number;
    count: number;
    page: number;
    limit: number;
    data: PromotionDeductionProps[];
  };
}

export class PromotionRepository implements IPromotionRepository {
  constructor(private readonly client: ApolloClient) {}

  private evictPromoDeductionMatchSummaryCache(cache: ApolloCache, deductionId: number): void {
    cache.evict({
      id: "ROOT_QUERY",
      fieldName: "promoDeductionMatchSummary",
      args: { params: { deductionId } },
    });
    cache.gc();
  }

  private writePromoDeductionMatchSummaryCache(
    cache: ApolloCache,
    deductionId: number,
    summaries: IPromoDeductionMatchSummary[]
  ): void {
    cache.writeQuery<IPromoDeductionMatchSummaryQueryResponse>({
      query: GET_PROMO_DEDUCTION_MATCH_SUMMARY,
      variables: { params: { deductionId } },
      data: { promoDeductionMatchSummary: summaries },
    });
  }

  async getPromoDeductionMatchSummary(
    params: IGetPromoDeductionMatchSummaryParams,
    refetch = false
  ): Promise<IPromoDeductionMatchSummary[]> {
    const { data } = await this.client.query<IPromoDeductionMatchSummaryQueryResponse>({
      query: GET_PROMO_DEDUCTION_MATCH_SUMMARY,
      variables: { params: { deductionId: params.deductionId } },
      fetchPolicy: refetch ? "network-only" : "cache-first",
    });

    if (!data?.promoDeductionMatchSummary) {
      throw new ApplicationError("Failed to load promo deduction match summary");
    }

    return data.promoDeductionMatchSummary;
  }

  private evictAppliedDeductionCache(cache: ApolloCache, deductionId: number, remittanceItemId: number): void {
    cache.evict({
      id: cache.identify({
        __typename: "DeductionType",
        id: deductionId,
      }),
    });
    cache.evict({
      id: "ROOT_QUERY",
      fieldName: "deductions",
      args: {
        params: { remittance_item_id: remittanceItemId },
      },
    });
    cache.gc();
  }

  async putPromoDeductionApplied(params: IPutPromoDeductionAppliedParams): Promise<IPutPromoDeductionAppliedResult> {
    const { deductionId, matchResultId, reversalReason } = params;
    let appliedResult: { summaries: IPromoDeductionMatchSummary[]; deduction: DeductionProps } = { summaries: [], deduction: {} as DeductionProps };
    const { error } = await this.client.mutate<IPutPromoDeductionAppliedMutationResponse>({
      mutation: PUT_PROMO_DEDUCTION_APPLIED,
      variables: {
        params: {
          deductionId,
          matchResultId,
          ...(reversalReason ? { reversalReason } : {}),
        },
      },
      update: (cache, { data: mutationData }) => {
        const payload = mutationData?.putPromoDeductionApplied;
        if (!payload) {
          return;
        }
        appliedResult = {
          summaries: payload.summaries,
          deduction: JSON.parse(JSON.stringify(payload.deduction)) as DeductionProps,
        };
        this.evictPromoDeductionMatchSummaryCache(cache, deductionId);
        this.writePromoDeductionMatchSummaryCache(cache, deductionId, payload.summaries);
        this.evictAppliedDeductionCache(cache, payload.deduction.id, payload.deduction.remittanceItemId);
      },
    });

    if (!appliedResult) {
      throw new Error(error?.message || "Failed to apply promotion to deduction");
    }

    return {
      summaries: appliedResult.summaries,
      deduction: new Deduction(appliedResult.deduction),
    };
  }

  async getPromotions(params: IGetPromosParams, refetch = false): Promise<IPromoPaginatedResponse> {
    const { data } = await this.client.query<IPromotionsQueryResponse>({
      query: GET_PROMOTIONS,
      variables: { params },
      fetchPolicy: refetch ? "network-only" : "cache-first",
    });

    if (!data?.promotions) {
      throw new ApplicationError("Failed to load promotions");
    }

    return data.promotions;
  }

  async getPromoProductsByPromoEventId(
    params: IGetPromoProductsByPromoEventIdParams,
    refetch = false
  ): Promise<IPromoProductsByPromoEventIdPaginatedResponse> {
    const { data } = await this.client.query<IPromoProductsByPromoEventIdQueryResponse>({
      query: GET_PROMO_PRODUCTS_BY_PROMO_EVENT_ID,
      variables: { params },
      fetchPolicy: refetch ? "network-only" : "cache-first",
    });

    if (!data?.getPromoProductsByPromoEventId) {
      throw new ApplicationError("Failed to load promo products by promo event id");
    }

    return data.getPromoProductsByPromoEventId;
  }

  private normalizeMatchSummary(raw: Partial<IPromoMatchSummary>): IPromoMatchSummary {
    return {
      isBestMatch: raw.isBestMatch ?? false,
      matchStatus: raw.matchStatus ?? "",
      confidenceScore: raw.confidenceScore ?? 0,
      rawScore: raw.rawScore ?? 0,
      maxApplicableScore: raw.maxApplicableScore ?? 0,
      displayMatchStatus: raw.displayMatchStatus ?? "",
      appliedAt: raw.appliedAt ?? "",
      applicationSource: raw.applicationSource ?? "",
    };
  }

  private normalizePromotionDeduction(raw: PromotionDeductionProps): PromotionDeductionProps {
    return {
      ...raw,
      deduction: {
        ...raw.deduction,
        checkNumber: raw.deduction.checkNumber ?? null,
        invoiceNumber: raw.deduction.invoiceNumber ?? null,
        invoiceDate: raw.deduction.invoiceDate ?? null,
        analystUser: {
          userId: raw.deduction.analystUser?.userId ?? null,
          fullName: raw.deduction.analystUser?.fullName ?? "",
          avatar: raw.deduction.analystUser?.avatar ?? null,
          isDeleted: raw.deduction.analystUser?.isDeleted ?? false,
        },
      },
      matchSummary: this.normalizeMatchSummary(raw.matchSummary),
      application: {
        appliedId: raw.application.appliedId,
        appliedAt: raw.application.appliedAt,
        applicationSource: raw.application.applicationSource,
      },
    };
  }

  async getPromoDeductionApplySummary(
    params: IGetPromoDeductionApplySummaryParams,
    refetch = false
  ): Promise<IPromoDeductionApplySummaryResponse> {
    const { data } = await this.client.query<IGetPromoDeductionApplySummaryQueryResponse>({
      query: GET_PROMO_DEDUCTION_APPLY_SUMMARY,
      variables: { params },
      fetchPolicy: refetch ? "network-only" : "cache-first",
    });

    if (!data?.getPromoDeductionApplySummary) {
      throw new ApplicationError("Failed to load promo deduction apply summary");
    }

    const summary = data.getPromoDeductionApplySummary;

    return {
      totalDeductionsClaimed: summary.totalDeductionsClaimed,
      totalDeductionsAmount: summary.totalDeductionsAmount,
      count: summary.count,
      page: summary.page,
      limit: summary.limit,
      data: summary.data.map(row => this.normalizePromotionDeduction(row)),
    };
  }
}
